import type { Kart } from '../game/kart';
import { mixColor } from '../core/math';
import { TEAM_COLORS } from '../game/world';

/** Lekerekített téglalap, középpontos koordinátákkal. */
const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void => {
  const rr = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
};

const wheel = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void => {
  ctx.fillStyle = '#15171d';
  roundRect(ctx, x - w / 2, y - h / 2, w, h, 2);
  ctx.fill();
  ctx.fillStyle = '#3a3f4a';
  roundRect(ctx, x - w / 2 + 1, y - h / 2 + 1, w - 2, h * 0.4, 1.5);
  ctx.fill();
};

const driver = (ctx: CanvasRenderingContext2D, kart: Kart, x: number, y: number, r: number): void => {
  const c = kart.character.colors;
  ctx.fillStyle = c.skin;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(16,19,28,0.55)';
  ctx.lineWidth = 1.4;
  ctx.stroke();
  // haj
  ctx.fillStyle = c.hair;
  ctx.beginPath();
  ctx.arc(x - r * 0.18, y, r * 0.92, Math.PI * 0.55, Math.PI * 1.55);
  ctx.fill();
  // napszemüveg / szem
  ctx.fillStyle = 'rgba(16,19,28,0.75)';
  ctx.fillRect(x + r * 0.1, y - r * 0.28, r * 0.9, r * 0.5);
};

const car = (ctx: CanvasRenderingContext2D, kart: Kart): void => {
  const c = kart.character.colors;
  const L = kart.character.vehicle.length;
  const W = kart.character.vehicle.width;
  const body = c.body;
  const dark = mixColor(body, '#10131c', 0.5);
  const light = mixColor(body, '#ffffff', 0.38);

  // karosszéria
  ctx.fillStyle = body;
  roundRect(ctx, -L / 2, -W / 2, L, W, W * 0.24);
  ctx.fill();
  ctx.strokeStyle = dark;
  ctx.lineWidth = 2;
  ctx.stroke();

  // motorháztető él
  ctx.fillStyle = light;
  ctx.globalAlpha = 0.5;
  roundRect(ctx, L * 0.18, -W * 0.38, L * 0.22, W * 0.76, W * 0.14);
  ctx.fill();
  ctx.globalAlpha = 1;

  // szélvédő
  ctx.fillStyle = '#8fd0e6';
  roundRect(ctx, L * 0.02, -W * 0.33, L * 0.14, W * 0.66, W * 0.1);
  ctx.fill();

  // tető
  ctx.fillStyle = light;
  roundRect(ctx, -L * 0.2, -W * 0.4, L * 0.32, W * 0.8, W * 0.16);
  ctx.fill();
  ctx.strokeStyle = dark;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // hátsó ablak
  ctx.fillStyle = 'rgba(143,208,230,0.7)';
  roundRect(ctx, -L * 0.24, -W * 0.28, L * 0.05, W * 0.56, 2);
  ctx.fill();

  // kerekek
  wheel(ctx, L * 0.31, -W / 2 - 1, L * 0.16, W * 0.18);
  wheel(ctx, L * 0.31, W / 2 + 1, L * 0.16, W * 0.18);
  wheel(ctx, -L * 0.3, -W / 2 - 1, L * 0.16, W * 0.18);
  wheel(ctx, -L * 0.3, W / 2 + 1, L * 0.16, W * 0.18);

  // lámpák
  ctx.fillStyle = '#ffe9a8';
  ctx.fillRect(L / 2 - 4, -W * 0.38, 3.5, W * 0.16);
  ctx.fillRect(L / 2 - 4, W * 0.22, 3.5, W * 0.16);
  ctx.fillStyle = '#ff5c4d';
  ctx.fillRect(-L / 2 + 1, -W * 0.38, 2.5, W * 0.14);
  ctx.fillRect(-L / 2 + 1, W * 0.24, 2.5, W * 0.14);

  const kind = kart.character.vehicle.kind;
  if (kind === 'taxi') {
    ctx.fillStyle = '#ffc857';
    roundRect(ctx, -L * 0.02, -W * 0.12, L * 0.16, W * 0.24, 2);
    ctx.fill();
  }
  if (kind === 'courier') {
    ctx.fillStyle = kart.character.colors.accent;
    roundRect(ctx, -L * 0.4, -W * 0.42, L * 0.2, W * 0.84, 4);
    ctx.fill();
    ctx.strokeStyle = dark;
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }
  if (kind === 'van' || kind === 'minivan') {
    ctx.strokeStyle = 'rgba(16,19,28,0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-L * 0.04, -W * 0.4);
    ctx.lineTo(-L * 0.04, W * 0.4);
    ctx.stroke();
  }

  driver(ctx, kart, L * 0.05, 0, W * 0.15);
};

const kartVehicle = (ctx: CanvasRenderingContext2D, kart: Kart): void => {
  const c = kart.character.colors;
  const L = kart.character.vehicle.length;
  const W = kart.character.vehicle.width;
  const dark = mixColor(c.body, '#10131c', 0.55);

  // váz
  ctx.strokeStyle = dark;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(-L * 0.42, -W * 0.32);
  ctx.lineTo(L * 0.34, -W * 0.28);
  ctx.moveTo(-L * 0.42, W * 0.32);
  ctx.lineTo(L * 0.34, W * 0.28);
  ctx.moveTo(-L * 0.05, -W * 0.32);
  ctx.lineTo(-L * 0.05, W * 0.32);
  ctx.stroke();

  // ülés + motor
  ctx.fillStyle = c.body;
  roundRect(ctx, -L * 0.34, -W * 0.26, L * 0.42, W * 0.52, 5);
  ctx.fill();
  ctx.fillStyle = dark;
  roundRect(ctx, -L * 0.42, -W * 0.2, L * 0.12, W * 0.4, 3);
  ctx.fill();

  // orr
  ctx.fillStyle = c.accent;
  roundRect(ctx, L * 0.18, -W * 0.3, L * 0.2, W * 0.6, 4);
  ctx.fill();

  wheel(ctx, L * 0.33, -W * 0.52, L * 0.2, 6);
  wheel(ctx, L * 0.33, W * 0.52, L * 0.2, 6);
  wheel(ctx, -L * 0.34, -W * 0.55, L * 0.22, 7);
  wheel(ctx, -L * 0.34, W * 0.55, L * 0.22, 7);

  driver(ctx, kart, -L * 0.02, 0, W * 0.2);

  // kormány
  ctx.strokeStyle = '#2a2e38';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(L * 0.12, 0, W * 0.16, 0, Math.PI * 2);
  ctx.stroke();
};

const scooter = (ctx: CanvasRenderingContext2D, kart: Kart): void => {
  const c = kart.character.colors;
  const L = kart.character.vehicle.length;
  const W = kart.character.vehicle.width;
  const dark = mixColor(c.body, '#10131c', 0.5);

  // deck
  ctx.fillStyle = dark;
  roundRect(ctx, -L * 0.4, -W * 0.16, L * 0.8, W * 0.32, 4);
  ctx.fill();
  ctx.fillStyle = c.body;
  roundRect(ctx, -L * 0.24, -W * 0.2, L * 0.46, W * 0.4, 4);
  ctx.fill();

  wheel(ctx, L * 0.38, 0, 7, W * 0.5);
  wheel(ctx, -L * 0.38, 0, 7, W * 0.5);

  // kormányrúd
  ctx.strokeStyle = '#2a2e38';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(L * 0.26, 0);
  ctx.lineTo(L * 0.34, 0);
  ctx.stroke();
  ctx.fillStyle = c.accent;
  ctx.beginPath();
  ctx.arc(L * 0.3, 0, 3, 0, Math.PI * 2);
  ctx.fill();

  // álló sofőr
  ctx.fillStyle = c.body;
  roundRect(ctx, -L * 0.12, -W * 0.34, L * 0.14, W * 0.68, 4);
  ctx.fill();
  driver(ctx, kart, L * 0.02, 0, W * 0.26);
};

export function drawKart(ctx: CanvasRenderingContext2D, kart: Kart, time: number): void {
  if (kart.finished) return;
  if (kart.out || kart.respawnTimer > 0) return;
  const kind = kart.character.vehicle.kind;
  const len = kart.character.vehicle.length;
  const wid = kart.character.vehicle.width;

  // battle: csapatszínű gyűrű a kocsi alatt
  if (kart.battleMode) {
    ctx.save();
    ctx.strokeStyle = TEAM_COLORS[kart.team];
    ctx.globalAlpha = 0.85;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.ellipse(kart.pos.x, kart.pos.y + 3, Math.max(len, wid) * 0.62, Math.max(len, wid) * 0.5, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // pajzs
  if (kart.shieldTimer > 0) {
    const pulse = 0.55 + Math.sin(time * 10) * 0.15;
    ctx.save();
    ctx.strokeStyle = `rgba(62,198,192,${pulse.toFixed(3)})`;
    ctx.fillStyle = `rgba(62,198,192,0.13)`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(kart.pos.x, kart.pos.y, Math.max(len, wid) * 0.62, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // árnyék
  ctx.save();
  ctx.translate(kart.pos.x + 3, kart.pos.y + 6);
  ctx.rotate(kart.heading);
  ctx.fillStyle = 'rgba(8,10,16,0.3)';
  roundRect(ctx, -len / 2, -wid / 2, len, wid, wid * 0.3);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.translate(kart.pos.x, kart.pos.y - kart.hopPhase * 9);
  ctx.rotate(kart.heading);

  if (kart.boostTimer > 0) {
    const flame = 10 + Math.sin(time * 30) * 3;
    ctx.fillStyle = 'rgba(255,180,60,0.85)';
    ctx.beginPath();
    ctx.moveTo(-len / 2 - 2, -wid * 0.18);
    ctx.lineTo(-len / 2 - flame, 0);
    ctx.lineTo(-len / 2 - 2, wid * 0.18);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,220,0.9)';
    ctx.beginPath();
    ctx.moveTo(-len / 2 - 2, -wid * 0.08);
    ctx.lineTo(-len / 2 - flame * 0.55, 0);
    ctx.lineTo(-len / 2 - 2, wid * 0.08);
    ctx.closePath();
    ctx.fill();
  }

  if (kind === 'kart') kartVehicle(ctx, kart);
  else if (kind === 'scooter') scooter(ctx, kart);
  else car(ctx, kart);

  ctx.restore();

  if (kart.isPlayer) {
    const label = `P${kart.playerIndex + 1}`;
    ctx.save();
    ctx.font = '700 11px "Trebuchet MS", sans-serif';
    ctx.textAlign = 'center';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(10,12,20,0.75)';
    ctx.strokeText(label, kart.pos.x, kart.pos.y - wid / 2 - 12);
    ctx.fillStyle = kart.character.colors.accent;
    ctx.fillText(label, kart.pos.x, kart.pos.y - wid / 2 - 12);
    ctx.restore();

    // lassulás / járdán haladás / boost jelzése a játékos felett
    const boosted = kart.surface === 'boost';
    const warned = kart.surface === 'sidewalk' || kart.surface === 'kerb';
    if (kart.isPlayer && (kart.slowTimer > 0 || warned || boosted)) {
      const text =
        kart.slowTimer > 0
          ? `LASSÚ ${kart.slowTimer.toFixed(1)}`
          : boosted
            ? 'BOOST'
            : kart.surface === 'sidewalk'
              ? 'JÁRDA'
              : 'SZEGÉLY';
      const color = kart.slowTimer > 0 ? '#8ec5ff' : boosted ? '#ffc857' : '#efb14a';
      ctx.save();
      ctx.font = '800 10px "Trebuchet MS", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const w = ctx.measureText(text).width + 14;
      const h = 16;
      const bx = kart.pos.x - w / 2;
      const by = kart.pos.y - wid / 2 - 40;
      ctx.fillStyle = 'rgba(16,19,28,0.86)';
      roundRect(ctx, bx, by, w, h, 8);
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.fillText(text, kart.pos.x, by + h / 2 + 0.5);
      ctx.restore();
    }
  }
}
